<template>
    <app-layout class="billing-container">
        <common-content-spacer height="medium">
            <common-breadcrumbs :breadcrumbs="pageBreadcrumbs"></common-breadcrumbs>
            <common-page-header :heading="pageTitle" size="medium"></common-page-header>
            <common-page-split spacing="medium">
                <template #aside>
                    <common-content-group :label="$t('pages.practice.billing.active_plan')">
                        <common-content-spacer height="medium">
                            <div>
                                <common-page-header
                                    :heading="planHeading"
                                    size="page-plain-header"
                                    contentSpacing="medium"
                                    :isCenter="true"
                                ></common-page-header>
                                <p v-if="!$auth.isPracticePremium()" class="billing-container--side-info">
                                    {{ $t('pages.practice.billing.billing_side_info') }}
                                </p>
                                <p v-else class="billing-container--side-info">
                                    {{ $t('pages.practice.billing.billing_side_info_premium') }}
                                </p>
                            </div>
                            <billing-features-list :featuresList="featuresList"></billing-features-list>
                            <p v-if="$auth.isPracticeTrial()" class="billing-container--side-features--info">
                                {{ $t('pages.practice.billing.free_trial_ends', { date: freeTrialPeriod }) }}
                            </p>
                            <common-buttons v-if="showUpgradeOrChange">
                                <common-button
                                    :label="primaryCtaLabel"
                                    size="large"
                                    type="primary"
                                    @click="toggleBillingPricingModal"
                                ></common-button>
                            </common-buttons>
                            <p
                                v-if="$auth.isPracticePremium()"
                                class="billing-container--side-features--cancel"
                                @click="openAreYouSureModal"
                            >
                                {{ $t('pages.practice.billing.cancel_subscription') }}
                            </p>
                        </common-content-spacer>
                    </common-content-group>
                </template>
                <template #main>
                    <plan-usage-panel v-if="showUsage" :usage="usage"></plan-usage-panel>
                    <billing-payment-method v-if="$auth.isPracticePremium()" :paymentInfo="paymentInfo"></billing-payment-method>
                    <billing-table :invoiceData="invoiceData"></billing-table>
                </template>
            </common-page-split>
        </common-content-spacer>
        <billing-pricing-modal
            v-if="billingPricingModalOpen"
            mode="subscription"
            @close="toggleBillingPricingModal"
            @subscription-created="subscriptionCreated"
        ></billing-pricing-modal>
        <common-are-you-sure-modal
            v-if="isAreYouSureModalOpen"
            :headingText="$t('pages.referrals.show.disclaimer_modal.heading')"
            :contentText="cancelModalText"
            :noButtonText="$t('common.microcopy.false')"
            :yesButtonText="$t('common.microcopy.true')"
            :showWarningIcon="true"
            @close="isAreYouSureModalOpen = false"
            @continue="cancelSubscription"
        ></common-are-you-sure-modal>
        <common-notification ref="commonSuccessNotification" :content="notificationMessage"></common-notification>
    </app-layout>
</template>

<script>
    import AppLayout from 'components/layouts/AppLayout'
    import PageWithDataMixin from 'mixins/PageWithDataMixin'
    import BillingPaymentMethod from 'components/page-elements/practice/billing/BillingPaymentMethod'
    import BillingTable from 'components/page-elements/practice/billing/BillingTable'
    import BillingFeaturesList from 'components/page-elements/practice/billing/BillingFeaturesList'
    import PlanUsagePanel from 'components/page-elements/practice/billing/PlanUsagePanel'
    import PracticesAPI from 'api/PracticesAPI'
    import BillingPricingModal from 'components/page-elements/practice/billing/BillingPricingModal'
    import PracticePlanEnum from '@/enums/PracticePlanEnum'
    import dayjs from 'dayjs'
    import { getEffectivePlanCode, getPlanDisplayName, isCustomPlan, isPlanEngine } from 'services/billingPlanHelpers'

    export default {
        mixins: [PageWithDataMixin],
        components: {
            AppLayout,
            BillingTable,
            BillingPaymentMethod,
            BillingFeaturesList,
            BillingPricingModal,
            PlanUsagePanel,
        },

        data() {
            return {
                invoiceData: [],
                paymentInfo: {},
                usage: null,
                billingPricingModalOpen: false,
                isAreYouSureModalOpen: false,
                notificationMessage: '',
                lastSubscribedPlanName: null,
            }
        },
        computed: {
            practice() {
                return this.$auth.getPractice() || {}
            },
            planCode() {
                return getEffectivePlanCode(this.practice)
            },
            planHeading() {
                return getPlanDisplayName(this.planCode)
            },
            pageTitle() {
                return this.$t('pages.practice.billing.titles.plan_billing', { practiceName: this.practice.name })
            },
            pageBreadcrumbs() {
                return [
                    {
                        label: this.$t('pages.practice.billing.breadcrumbs.practice'),
                        route: { name: 'practice.profile' },
                    },
                    {
                        label: this.$t('pages.practice.billing.breadcrumbs.practice_billing'),
                    },
                ]
            },
            freeTrialPeriod() {
                return dayjs(this.practice.trialPeriodEnds).format('MMM DD YYYY')
            },
            showUsage() {
                return isPlanEngine(this.practice) && this.usage
            },
            showUpgradeOrChange() {
                if (isCustomPlan(this.planCode)) {
                    return false
                }
                return true
            },
            primaryCtaLabel() {
                if (this.$auth.isPracticePremium()) {
                    return this.$t('pages.practice.billing.change_plan')
                }
                return this.$t('pages.practice.billing.upgrade_now')
            },
            cancelModalText() {
                return this.$t('pages.practice.billing.cancel_subscription_info', {
                    activeBillingPeriod: this.practice.planInterval || this.practice.activeBillingPeriod,
                    subscription: this.planHeading,
                })
            },
            featuresList() {
                if (this.planCode === PracticePlanEnum.FREE) {
                    return [
                        this.$t('pages.practice.billing.billing_list.item_1'),
                        this.$t('pages.practice.billing.billing_list.item_2'),
                        this.$t('pages.practice.billing.billing_list.item_3'),
                        this.$t('pages.practice.billing.billing_list.item_4'),
                        this.$t('pages.practice.billing.billing_list.item_5'),
                        this.$t('pages.practice.billing.billing_list.item_6'),
                        this.$t('pages.practice.billing.billing_list.item_7'),
                        this.$t('pages.practice.billing.billing_list.item_8'),
                        this.$t('pages.practice.billing.billing_list.item_9'),
                    ]
                }
                return [
                    this.$t('pages.practice.billing.payment_form_list.item_1'),
                    this.$t('pages.practice.billing.payment_form_list.item_2'),
                    this.$t('pages.practice.billing.payment_form_list.item_3'),
                    this.$t('pages.practice.billing.payment_form_list.item_4'),
                    this.$t('pages.practice.billing.payment_form_list.item_5'),
                    this.$t('pages.practice.billing.payment_form_list.item_6'),
                    this.$t('pages.practice.billing.payment_form_list.item_7'),
                    this.$t('pages.practice.billing.payment_form_list.item_8'),
                    this.$t('pages.practice.billing.payment_form_list.item_9'),
                    this.$t('pages.practice.billing.payment_form_list.item_10'),
                ]
            },
        },

        methods: {
            getInvoicesData() {
                PracticesAPI.getInvoicesData().then((response) => {
                    this.invoiceData = response.invoices
                    this.paymentInfo = response.card
                })
            },

            loadUsage() {
                if (!isPlanEngine(this.practice)) {
                    this.usage = this.practice.usage || null
                    return
                }
                PracticesAPI.getPlanUsage()
                    .then((response) => {
                        this.usage = response
                    })
                    .catch(() => {
                        this.usage = this.practice.usage || null
                    })
            },

            cancelSubscription() {
                const request = isPlanEngine(this.practice) ? PracticesAPI.downgradeToFree() : PracticesAPI.cancelSubscription()

                request.then(() => {
                    this.isAreYouSureModalOpen = false
                    this.notificationMessage = this.$t('pages.practice.billing.subscription_canceled')
                    this.$refs.commonSuccessNotification.showNotification()
                    this.$auth.forceUpdatePractice().then(() => this.loadUsage())
                })
            },

            openAreYouSureModal() {
                this.isAreYouSureModalOpen = true
            },

            subscriptionCreated(plan) {
                const name = plan?.name || plan?.plan?.name || this.planHeading
                this.notificationMessage = this.$t('pages.practice.billing.subscription_created', { planName: name })
                this.$refs.commonSuccessNotification.showNotification()
                this.toggleBillingPricingModal()
                this.loadUsage()
            },

            toggleBillingPricingModal() {
                this.billingPricingModalOpen = !this.billingPricingModalOpen
                if (this.billingPricingModalOpen) {
                    document.body.classList.add('no-scroll')
                } else {
                    document.body.classList.remove('no-scroll')
                }
            },
        },

        mounted() {
            this.getInvoicesData()
            this.loadUsage()
        },
    }
</script>

<style scoped lang="scss">
    @import 'styles/components/practice-billing/_practice-billing.scss';
</style>
